import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { Download, Loader2, RefreshCw } from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui";
import {
  GetBatchChangeRequestsJobDryRunResultDocument,
  type GetBatchChangeRequestsJobDryRunResultQuery,
} from "~/features/dataImportProjects/gqls";
import { useExportBatchChangeRequestsJobDryRunResult } from "~/features/dataImportProjects/hooks/useExportBatchChangeRequestsJobDryRunResult";
import { useTenant } from "~/features/tenant/context";
import { JsonDiff, JsonKeyValues, type JsonRecord } from "./BatchEvidencesJobJson";

type DryRunOutput = Extract<
  GetBatchChangeRequestsJobDryRunResultQuery["cnutGetBatchChangeRequestsJobDryRunResult"],
  { __typename?: "DryRunBatchChangeRequestsJobOutput" }
>;
type DryRunEntry = DryRunOutput["entities"][number] | DryRunOutput["relations"][number];

function DryRunEntryCard({ entry, kind }: { entry: DryRunEntry; kind: "entity" | "relation" }) {
  const { t } = useTranslation();
  if (!entry.details) {
    return null;
  }
  const model = entry.details?.model;
  const changeRequest = entry.details?.changeRequest;

  // Deactivate-mode change requests preserve all properties and only flip deactivatedAt, so the
  // properties/metadata diffs below render empty. Surface the lifecycle change explicitly instead.
  const modelDeactivated = Boolean(model?.deactivatedAt);
  const changeRequestDeactivated = Boolean(changeRequest?.deactivatedAt);
  const willDeactivate = Boolean(changeRequest) && changeRequestDeactivated && !modelDeactivated;
  const willReactivate = Boolean(changeRequest) && !changeRequestDeactivated && modelDeactivated;

  return (
    <article className="rounded-[var(--radius-md)] border border-border-default p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge tone={kind === "entity" ? "primary" : "status-review"}>{kind}</Badge>
        <Badge tone={entry.changeType === "create" ? "score-a" : "default"}>{entry.changeType}</Badge>
        <Badge tone="default">{entry.changeRequestOperation}</Badge>
        {willDeactivate ? (
          <Badge tone="score-c">
            {t("feature.batchChangeRequestsJobs.dryRun.willDeactivate")}
            {changeRequest?.deactivatedAt ? ` (${changeRequest.deactivatedAt})` : null}
          </Badge>
        ) : null}
        {willReactivate ? (
          <Badge tone="score-a">{t("feature.batchChangeRequestsJobs.dryRun.willReactivate")}</Badge>
        ) : null}
        <span className="font-mono text-12-regular text-fg-3">{entry.changeRequestId || entry.modelId || "-"}</span>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <section className="rounded-[var(--radius-md)] border border-border-default p-3">
          <h4 className="mb-2 text-12-bold text-fg-2">{t("ui.properties")}</h4>
          {model?.properties && changeRequest?.properties ? (
            <JsonDiff before={model.properties as JsonRecord} after={changeRequest.properties as JsonRecord} />
          ) : (
            <JsonKeyValues value={(changeRequest?.properties ?? model?.properties ?? {}) as JsonRecord} />
          )}
        </section>
        <section className="rounded-[var(--radius-md)] border border-border-default p-3">
          <h4 className="mb-2 text-12-bold text-fg-2">{t("model.common.metadata")}</h4>
          {model?.metadata && changeRequest?.metadata ? (
            <JsonDiff before={model.metadata as JsonRecord} after={changeRequest.metadata as JsonRecord} />
          ) : (
            <JsonKeyValues value={(changeRequest?.metadata ?? model?.metadata ?? {}) as JsonRecord} />
          )}
        </section>
      </div>
      {"source" in entry.details && entry.details.source ? (
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <section className="rounded-[var(--radius-md)] border border-border-default p-3">
            <h4 className="mb-2 text-12-bold text-fg-2">{t("model.record.relation.source")}</h4>
            <JsonKeyValues value={(entry.details.source.properties ?? {}) as JsonRecord} />
          </section>
          <section className="rounded-[var(--radius-md)] border border-border-default p-3">
            <h4 className="mb-2 text-12-bold text-fg-2">{t("model.record.relation.target")}</h4>
            <JsonKeyValues value={(entry.details.target?.properties ?? {}) as JsonRecord} />
          </section>
        </div>
      ) : null}
    </article>
  );
}

function DryRunResultContent({ jobRunId, refetchKey }: { jobRunId: string; refetchKey: number }) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const { data, dataState } = useSuspenseQuery(
    GetBatchChangeRequestsJobDryRunResultDocument,
    tenantId
      ? {
          variables: { input: { tenantId, jobRunId } },
        }
      : skipToken,
  );
  if (!tenantId || dataState !== "complete" || !data) {
    return null;
  }
  const result = data.cnutGetBatchChangeRequestsJobDryRunResult;

  void refetchKey;

  if (result.__typename === "ValidationError") {
    return (
      <section className="rounded-[var(--radius-md)] border border-border-default p-4">
        <h3 className="text-14-bold text-fg-1">{t("feature.errors.errorFallback.errorDetails")}</h3>
        <dl className="mt-3 grid gap-2">
          <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
            <dt className="text-12-regular text-fg-3">{t("ui.code")}</dt>
            <dd className="text-14-regular text-fg-1">{result.code}</dd>
          </div>
          <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
            <dt className="text-12-regular text-fg-3">{t("ui.message")}</dt>
            <dd className="text-14-regular text-fg-1">{result.message}</dd>
          </div>
        </dl>
      </section>
    );
  }
  if (result.__typename !== "DryRunBatchChangeRequestsJobOutput") {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  return (
    <section className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-[var(--radius-md)] border border-border-default p-4">
          <p className="text-12-regular text-fg-3">{t("model.record.entity.modelNames")}</p>
          <p className="text-[24px] font-semibold text-fg-1">{result.entities.length}</p>
        </div>
        <div className="rounded-[var(--radius-md)] border border-border-default p-4">
          <p className="text-12-regular text-fg-3">{t("model.record.relation.modelNames")}</p>
          <p className="text-[24px] font-semibold text-fg-1">{result.relations.length}</p>
        </div>
      </div>
      {result.entities.length === 0 && result.relations.length === 0 ? (
        <div className="rounded-[var(--radius-md)] border border-border-default p-4">
          <h3 className="text-14-bold text-fg-1">{t("feature.batchEvidencesJobs.alert.noChangeRequestsCreated")}</h3>
          <p className="mt-1 text-12-regular text-fg-3">
            {t("feature.batchEvidencesJobs.alert.noChangeRequestsCreatedDescription")}
          </p>
        </div>
      ) : null}
      {result.entities.map((entry, index) => (
        <DryRunEntryCard key={`entity-${entry.changeRequestId ?? index}`} entry={entry} kind="entity" />
      ))}
      {result.relations.map((entry, index) => (
        <DryRunEntryCard key={`relation-${entry.changeRequestId ?? index}`} entry={entry} kind="relation" />
      ))}
    </section>
  );
}

export function BatchChangeRequestsJobDryRunResultDialog({
  open,
  onOpenChange,
  jobRunId,
  jobId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobRunId: string | null;
  jobId: string;
}) {
  const { t } = useTranslation();
  const [refetchKey, setRefetchKey] = useState(0);
  const { onExport, exporting } = useExportBatchChangeRequestsJobDryRunResult({
    jobRunId: jobRunId ?? "",
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <DialogTitle>
                {t("model.batchChangeRequestsJob.modelName")} {t("ui.dryRunResult")}
              </DialogTitle>
              <DialogDescription>
                {t("model.common.id")}: {jobId}
              </DialogDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setRefetchKey((prev) => prev + 1)}>
                <RefreshCw className="size-3.5" />
                {t("ui.refresh")}
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={exporting || !jobRunId} onClick={onExport}>
                <Download className="size-3.5" />
                {t("ui.export")}
              </Button>
            </div>
          </div>
        </DialogHeader>
        {jobRunId ? (
          // Local Suspense boundary: keep useSuspenseQuery's suspension inside the dialog so it
          // doesn't bubble to the app-level boundary and blank/reload the page behind the dialog.
          <Suspense
            fallback={
              <div className="flex justify-center py-10">
                <Loader2 className="size-5 animate-spin text-fg-3" />
              </div>
            }
          >
            <DryRunResultContent key={refetchKey} jobRunId={jobRunId} refetchKey={refetchKey} />
          </Suspense>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
